const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api').replace(/\/+$/, '');

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('medicaclic_admin_token') : null;

  const response = await fetch(`${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json() : null;

  if (!response.ok) {
    const message = payload?.message ?? `API request failed: ${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message);
  }

  return (payload ?? (undefined as T)) as T;
}

export async function fetchPractitionersFromApi(): Promise<Record<string, unknown>[]> {
  const payload = await apiRequest<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>('/practitioners');
  return Array.isArray(payload) ? payload : (payload.data ?? []);
}

export async function fetchSearchOptionsFromApi(): Promise<{ cities: string[]; specialties: string[]; categories: string[]; districts: string[] }> {
  return apiRequest<{ cities: string[]; specialties: string[]; categories: string[]; districts: string[] }>('/practitioners/options');
}

export async function loginAdmin(credentials: { email: string; password: string }): Promise<{ challenge_id: string; email: string; message: string }> {
  return apiRequest<{ challenge_id: string; email: string; message: string }>('/admin/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}

export async function verifyAdminLoginCode(challengeId: string, code: string): Promise<{ token: string; user: { id: number; name: string; email: string } }> {
  return apiRequest<{ token: string; user: { id: number; name: string; email: string } }>('/admin/verify-code', {
    method: 'POST',
    body: JSON.stringify({ challenge_id: challengeId, code }),
  });
}

export async function fetchAdminProfile(): Promise<{ user: { id: number; name: string; email: string } }> {
  return apiRequest<{ user: { id: number; name: string; email: string } }>('/admin/me');
}

export async function updateAdminProfile(payload: {
  name: string;
  email: string;
  current_password?: string;
  password?: string;
  password_confirmation?: string;
}): Promise<{ user: { id: number; name: string; email: string }; message: string }> {
  return apiRequest<{ user: { id: number; name: string; email: string }; message: string }>('/admin/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function fetchAdminPractitioners(): Promise<Record<string, unknown>[]> {
  const payload = await apiRequest<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>('/admin/practitioners');
  return Array.isArray(payload) ? payload : (payload.data ?? []);
}

export async function fetchAdminLocations(): Promise<Record<string, string[]>> {
  return apiRequest<Record<string, string[]>>('/admin/locations');
}

export async function createAdminLocations(payload: { city: string; districts: string[] }): Promise<Record<string, string[]>> {
  return apiRequest<Record<string, string[]>>('/admin/locations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createAdminCatalogOption(
  kind: 'specialties' | 'categories',
  name: string,
): Promise<{ name: string }> {
  return apiRequest<{ name: string }>(`/admin/${kind}`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function createAdminPractitioner(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>('/admin/practitioners', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminPractitioner(id: number | string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/admin/practitioners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function connectAdminPractitionerCalendar(id: number | string): Promise<{ authorization_url: string }> {
  return apiRequest<{ authorization_url: string }>(`/admin/practitioners/${id}/google-calendar/connect`, {
    method: 'POST',
  });
}

export async function deleteAdminPractitioner(id: number | string): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/admin/practitioners/${id}`, {
    method: 'DELETE',
  });
}

export async function fetchAdminAppointments(): Promise<Record<string, unknown>[]> {
  return apiRequest<Record<string, unknown>[]>('/admin/appointments');
}

export type AdminPractitionerRequest = {
  id: string;
  request_type: 'fiche' | 'quote' | 'visibility-study' | 'contact';
  request_label: string;
  name: string;
  email: string;
  phone: string;
  etablissement: string | null;
  specialite: string | null;
  category: string | null;
  ville: string | null;
  quartier: string | null;
  adresse: string | null;
  fonction: string | null;
  besoins: string | null;
  message: string | null;
  status: string;
  prospect_id: number | null;
  practitioner_id: number | null;
  created_at: string;
};

export async function fetchAdminPractitionerRequests(): Promise<AdminPractitionerRequest[]> {
  return apiRequest<AdminPractitionerRequest[]>('/admin/practitioner-requests');
}

export async function approveAdminPractitionerRequest(
  prospectId: number,
  payload: { subscription_tier: 'standard' | 'ars' | 'premium' | 'convention'; convention_code?: string },
): Promise<{ success: boolean; status: string; practitioner_id: number }> {
  return apiRequest<{ success: boolean; status: string; practitioner_id: number }>(`/admin/practitioner-requests/fiche/${prospectId}/approve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminAppointmentDecision(id: number | string, decision: 'confirmed' | 'cancelled'): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/admin/appointments/${id}/decision`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}

export async function fetchPractitionerById(id: string): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/practitioners/${id}`);
}

export async function fetchPractitionerSlots(practitionerId: string | number, date: string): Promise<{
  date: string;
  slots: { id: string; start_time: string; end_time: string }[];
}> {
  return apiRequest(`/practitioners/${practitionerId}/slots?date=${encodeURIComponent(date)}`);
}

export async function createAppointment(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>('/appointments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createPractitionerProspect(payload: Record<string, string>): Promise<{ success: boolean; prospect_id?: number; notification_sent?: boolean }> {
  return apiRequest('/practitioner-prospects', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createPractitionerContactRequest(payload: Record<string, string>): Promise<{ success: boolean; request_id?: number; notification_sent?: boolean }> {
  return apiRequest('/practitioner-contact-requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function lookupAppointment(token: string): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/appointments/lookup?token=${encodeURIComponent(token)}`);
}

export async function updateAppointmentDecision(
  appointmentId: number | string,
  decision: 'confirmed' | 'cancelled',
): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>(`/appointments/${appointmentId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
}
